// SecaoDeEntrega.tsx
//
// O CORAÇÃO DO CARD #93, na tela do dono do restaurante.
//
// Aqui ele decide, POR FILIAL, o que o cliente final vê enquanto o pedido está
// a caminho. O motivo é dele, e está escrito na tela nas palavras dele:
//
//   "quando dois ou mais pedidos saem juntos, a rota real mostra ao cliente que
//    o entregador passou em outro endereço antes"
//
// Dois modos:
//   PREVISAO   o cliente vê uma faixa de horário. Nenhuma posição.  (padrão)
//   ROTA_REAL  o cliente vê o entregador se movendo no mapa.        (etapa 2)
//
// ROTA_REAL APARECE DESABILITADO, COM O MOTIVO AO LADO — e isso é decisão, não
// falta de tempo. A coleta de posição do entregador exige consentimento, aviso
// permanente de rastreio, prazo de retenção e rotina de descarte; enquanto isso
// não existe, um rádio clicável faria o dono acreditar que ligou alguma coisa
// que não há. Opção que não faz nada é pior que opção ausente.
//
// Quem decide se ele está disponível é o SERVIDOR (`modosDisponiveis` vem na
// resposta) — no dia em que o rastreamento entrar no ar, este rádio destrava
// sozinho, sem ninguém precisar lembrar de mexer aqui.
//
// Estilo: os styled-components que esta pasta já usa. Nada de Tailwind nem de
// componente novo de terceiro — a tela é CRA legado e vai ser reescrita inteira
// quando o Central virar Next.

import React, { useEffect, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import {
  ConfigDeEntrega,
  EntregadorDaUnidade,
  ModoDeCompartilhamento,
  rotaService,
} from "../../../../services/rota.service";

interface Props {
  subsidiaryUuid: string;
  /** Os usuários do PDV, para o dono ESCOLHER em vez de digitar uuid. */
  usuariosDoPdv: { userUuid: string; userName: string }[];
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

/**
 * O exemplo calculado AO VIVO.
 *
 * Ele existe porque "15 minutos por entrega, 10 de folga" não diz nada sozinho:
 * o que o dono precisa ver é a frase que o cliente dele vai ler. Um número de
 * configuração que ninguém consegue traduzir em tela vira um número que ninguém
 * ajusta.
 */
function exemploDaFaixa(minutosPorEntrega: number, folga: number): string {
  const saida = new Date();
  saida.setHours(19, 12, 0, 0);
  const de = new Date(saida.getTime() + minutosPorEntrega * 60_000);
  const ate = new Date(de.getTime() + folga * 60_000);
  const hora = (data: Date) =>
    `${String(data.getHours()).padStart(2, "0")}h${String(data.getMinutes()).padStart(2, "0")}`;
  return `saiu às 19h12 → o cliente vê "chega entre ${hora(de)} e ${hora(ate)}"`;
}

const SecaoDeEntrega: React.FC<Props> = ({ subsidiaryUuid, usuariosDoPdv }) => {
  const [config, setConfig] = useState<ConfigDeEntrega | null>(null);
  const [modosDisponiveis, setModosDisponiveis] = useState<ModoDeCompartilhamento[]>(["PREVISAO"]);
  const [motivoRotaReal, setMotivoRotaReal] = useState<string | null>(null);
  const [entregadores, setEntregadores] = useState<EntregadorDaUnidade[]>([]);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [salvando, setSalvando] = useState(false);

  // O cadastro de um entregador novo.
  const [novoNome, setNovoNome] = useState("");
  const [novoTelefone, setNovoTelefone] = useState("");
  const [novoUsuario, setNovoUsuario] = useState("");
  // A senha inicial vive SÓ neste estado, e só até a tela ser recarregada: o
  // banco guarda o hash e não há como relê-la. Ver `entregador.service.ts`.
  const [senhaMostrada, setSenhaMostrada] = useState<{ nome: string; senha: string } | null>(null);

  useEffect(() => {
    let ativo = true;

    rotaService
      .buscarConfig(subsidiaryUuid)
      .then((dados) => {
        if (!ativo) return;
        setConfig(dados.config);
        setModosDisponiveis(dados.modosDisponiveis ?? ["PREVISAO"]);
        setMotivoRotaReal(dados.motivoRotaRealIndisponivel ?? null);
      })
      .catch(() => {
        /* sem a config a seção não aparece; o resto da tela de unidade continua */
      });

    rotaService
      .listarEntregadores(subsidiaryUuid)
      .then((dados) => {
        if (ativo) setEntregadores(dados.entregadores ?? []);
      })
      .catch(() => {
        /* lista vazia é o comportamento certo quando ela não carrega */
      });

    return () => {
      ativo = false;
    };
  }, [subsidiaryUuid]);

  const mudar = (mudanca: Partial<ConfigDeEntrega>) => {
    setConfig((atual) => (atual ? { ...atual, ...mudanca } : atual));
  };

  const salvar = async () => {
    if (!config) return;
    setSalvando(true);
    setAviso(null);
    try {
      const resposta = await rotaService.salvarConfig(subsidiaryUuid, {
        modoDeCompartilhamento: config.modoDeCompartilhamento,
        minutosDePreparo: config.minutosDePreparo,
        minutosPorEntrega: config.minutosPorEntrega,
        folgaDaFaixaMinutos: config.folgaDaFaixaMinutos,
        mostrarNomeDoEntregador: config.mostrarNomeDoEntregador,
      });
      setConfig(resposta.config);
      setAviso(
        resposta.aviso
          ? { tom: "atencao", texto: resposta.aviso }
          : { tom: "ok", texto: "Configuração de entrega salva." }
      );
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível salvar a configuração de entrega.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const cadastrar = async () => {
    setSalvando(true);
    setAviso(null);
    try {
      const resposta = await rotaService.criarEntregador(subsidiaryUuid, {
        nome: novoNome,
        telefone: novoTelefone,
        aotUserUuid: novoUsuario || null,
      });
      setEntregadores((atuais) => [
        ...atuais.filter((item) => item.entregadorId !== resposta.entregador.entregadorId),
        resposta.entregador,
      ]);
      setNovoNome("");
      setNovoTelefone("");
      setNovoUsuario("");
      if (resposta.senhaInicial) {
        setSenhaMostrada({ nome: resposta.entregador.nome, senha: resposta.senhaInicial });
      } else {
        setAviso({
          tom: "atencao",
          texto:
            "Esta pessoa já estava cadastrada em outra unidade e foi vinculada a esta. A senha dela não mudou.",
        });
      }
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível cadastrar o entregador.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const gerarSenha = async (entregador: EntregadorDaUnidade) => {
    setSalvando(true);
    try {
      const resposta = await rotaService.atualizarEntregador(entregador.entregadorId, {
        gerarSenha: true,
        subsidiaryUuid,
      });
      if (resposta.senhaInicial) {
        setSenhaMostrada({ nome: entregador.nome, senha: resposta.senhaInicial });
      }
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível gerar a senha.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (entregador: EntregadorDaUnidade) => {
    setSalvando(true);
    try {
      await rotaService.atualizarEntregador(entregador.entregadorId, {
        ativo: !entregador.ativo,
        subsidiaryUuid,
      });
      setEntregadores((atuais) =>
        atuais.map((item) =>
          item.entregadorId === entregador.entregadorId ? { ...item, ativo: !item.ativo } : item
        )
      );
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível alterar o entregador.",
      });
    } finally {
      setSalvando(false);
    }
  };

  if (!config) return null;

  const rotaRealDisponivel = modosDisponiveis.includes("ROTA_REAL");

  return (
    <div style={{ marginTop: "var(--sp-24)", borderTop: "1px solid var(--borda)", paddingTop: "var(--sp-16)" }}>
      <AlfredoStyles.CartaoTitulo as="h4">Entrega</AlfredoStyles.CartaoTitulo>
      <AlfredoStyles.CartaoSubtitulo>
        O que o cliente vê enquanto o pedido está a caminho. Os entregadores entram em{" "}
        <strong>rota.alfredo.food</strong> com o telefone e a senha.
      </AlfredoStyles.CartaoSubtitulo>

      {/* --- 1. o modo de compartilhamento --- */}
      <AlfredoStyles.LinhaCaixa style={{ marginTop: "var(--sp-12)", alignItems: "flex-start" }}>
        <AlfredoStyles.Caixa
          type="radio"
          name={`modo-${subsidiaryUuid}`}
          checked={config.modoDeCompartilhamento === "PREVISAO"}
          onChange={() => mudar({ modoDeCompartilhamento: "PREVISAO" })}
        />
        <span>
          <strong>Previsão de chegada</strong>
          <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
            O cliente vê a estimativa de horário. Não vê onde o entregador está.
          </AlfredoStyles.CampoAjuda>
        </span>
      </AlfredoStyles.LinhaCaixa>

      <AlfredoStyles.LinhaCaixa style={{ marginTop: "var(--sp-8)", alignItems: "flex-start" }}>
        <AlfredoStyles.Caixa
          type="radio"
          name={`modo-${subsidiaryUuid}`}
          disabled={!rotaRealDisponivel}
          checked={config.modoDeCompartilhamento === "ROTA_REAL"}
          onChange={() => mudar({ modoDeCompartilhamento: "ROTA_REAL" })}
        />
        <span style={{ opacity: rotaRealDisponivel ? 1 : 0.6 }}>
          <strong>Rota real no mapa</strong>
          <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
            {rotaRealDisponivel
              ? "O cliente acompanha o entregador se movendo no mapa."
              : motivoRotaReal}
          </AlfredoStyles.CampoAjuda>
        </span>
      </AlfredoStyles.LinhaCaixa>

      {/* O PORQUÊ DA ESCOLHA, nas palavras do dono. Sem esta frase o dono lê dois
          rádios técnicos e escolhe o que parecer mais moderno — que é o que
          expõe a operação dele. */}
      <AlfredoStyles.CampoAjuda style={{ display: "block", marginTop: "var(--sp-10)" }}>
        Quando dois ou mais pedidos saem juntos, a rota real mostra ao cliente que o entregador
        passou em outro endereço antes. Na previsão, o cliente vê só uma faixa de horário — nunca a
        posição dele na fila de entregas.
      </AlfredoStyles.CampoAjuda>

      {/* --- 2. os minutos --- */}
      <AlfredoStyles.Linha style={{ marginTop: "var(--sp-16)" }}>
        <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 190)" }}>
          <span>Tempo médio de uma entrega (min)</span>
          <AlfredoStyles.Entrada
            type="number"
            min={1}
            max={240}
            value={config.minutosPorEntrega}
            onChange={(e) => mudar({ minutosPorEntrega: Number(e.target.value) })}
          />
        </AlfredoStyles.Campo>
        <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 160)" }}>
          <span>Folga da faixa (min)</span>
          <AlfredoStyles.Entrada
            type="number"
            min={0}
            max={60}
            value={config.folgaDaFaixaMinutos}
            onChange={(e) => mudar({ folgaDaFaixaMinutos: Number(e.target.value) })}
          />
        </AlfredoStyles.Campo>
      </AlfredoStyles.Linha>

      <AlfredoStyles.CampoAjuda style={{ display: "block", marginTop: "var(--sp-6)" }}>
        {exemploDaFaixa(config.minutosPorEntrega, config.folgaDaFaixaMinutos)}. A faixa é congelada
        no momento em que o entregador marca "saí" — reordenar as paradas depois não muda o horário
        que o cliente já leu.
      </AlfredoStyles.CampoAjuda>

      {/* --- 3. o nome do entregador --- */}
      <AlfredoStyles.LinhaCaixa style={{ marginTop: "var(--sp-12)", alignItems: "flex-start" }}>
        <AlfredoStyles.Caixa
          type="checkbox"
          checked={config.mostrarNomeDoEntregador}
          onChange={(e) => mudar({ mostrarNomeDoEntregador: e.target.checked })}
        />
        <span>
          Mostrar o nome do entregador ao cliente
          <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
            Desligado por padrão: o nome de um funcionário para todo cliente é dado pessoal sem
            necessidade.
          </AlfredoStyles.CampoAjuda>
        </span>
      </AlfredoStyles.LinhaCaixa>

      <AlfredoStyles.Linha style={{ marginTop: "var(--sp-12)" }}>
        <AlfredoStyles.Button type="button" disabled={salvando} onClick={salvar}>
          {salvando ? "Salvando…" : "Salvar configuração de entrega"}
        </AlfredoStyles.Button>
      </AlfredoStyles.Linha>

      {/* --- 4. os entregadores --- */}
      <AlfredoStyles.CartaoTitulo as="h4" style={{ marginTop: "var(--sp-20)" }}>
        Entregadores desta unidade
      </AlfredoStyles.CartaoTitulo>

      {entregadores.length === 0 ? (
        <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
          Nenhum entregador cadastrado. Sem cadastro, ninguém consegue abrir o app da entrega.
        </AlfredoStyles.CampoAjuda>
      ) : (
        entregadores.map((entregador) => (
          <AlfredoStyles.Linha
            key={entregador.entregadorId}
            style={{ marginTop: "var(--sp-8)", alignItems: "center" }}
          >
            <span style={{ minWidth: "calc(var(--u) * 220)" }}>
              <strong>{entregador.nome}</strong>
              <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
                {entregador.telefone}
                {!entregador.ativo && " · desativado"}
                {entregador.ativo && entregador.precisaTrocarSenha && " · ainda não trocou a senha"}
                {!entregador.aotUserUuid && " · sem usuário do PDV: a lista dele fica vazia"}
              </AlfredoStyles.CampoAjuda>
            </span>
            <AlfredoStyles.Button type="button" disabled={salvando} onClick={() => gerarSenha(entregador)}>
              Gerar senha
            </AlfredoStyles.Button>
            <AlfredoStyles.Button type="button" disabled={salvando} onClick={() => alternarAtivo(entregador)}>
              {entregador.ativo ? "Desativar" : "Reativar"}
            </AlfredoStyles.Button>
          </AlfredoStyles.Linha>
        ))
      )}

      <AlfredoStyles.Linha style={{ marginTop: "var(--sp-12)", alignItems: "flex-end" }}>
        <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 180)" }}>
          <span>Nome</span>
          <AlfredoStyles.Entrada
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            placeholder="Claudio"
          />
        </AlfredoStyles.Campo>
        <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 170)" }}>
          <span>Telefone (com DDD)</span>
          <AlfredoStyles.Entrada
            value={novoTelefone}
            onChange={(e) => setNovoTelefone(e.target.value)}
            placeholder="11999999999"
          />
        </AlfredoStyles.Campo>
        <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 200)" }}>
          <span>Usuário dele no PDV</span>
          {/* Escolhido de uma lista, nunca digitado: o uuid errado aqui faz a
              lista do entregador nascer vazia para sempre, sem erro nenhum
              aparecer — porque nenhum pedido teria a linha tipo 6 dele. */}
          <AlfredoStyles.Seletor value={novoUsuario} onChange={(e) => setNovoUsuario(e.target.value)}>
            <option value="">—</option>
            {usuariosDoPdv.map((usuario) => (
              <option key={usuario.userUuid} value={usuario.userUuid}>
                {usuario.userName}
              </option>
            ))}
          </AlfredoStyles.Seletor>
        </AlfredoStyles.Campo>
        <AlfredoStyles.Button
          type="button"
          disabled={salvando || novoNome.trim().length < 2 || novoTelefone.replace(/\D/g, "").length < 10}
          onClick={cadastrar}
          style={{ marginBottom: "var(--sp-8)" }}
        >
          Cadastrar entregador
        </AlfredoStyles.Button>
      </AlfredoStyles.Linha>

      {senhaMostrada && (
        <AlfredoStyles.Aviso $tom="atencao">
          Senha de <strong>{senhaMostrada.nome}</strong>:{" "}
          <code style={{ fontSize: "1.1em", letterSpacing: "0.08em" }}>{senhaMostrada.senha}</code>
          <div style={{ marginTop: "var(--sp-6)" }}>
            {/* Dito em voz alta porque é verdade e porque é o que evita o
                chamado: o banco guarda só o hash. */}
            Anote e passe para ele agora — <strong>ela não pode ser vista de novo</strong>. No
            primeiro acesso o app vai pedir que ele crie uma senha própria.
          </div>
        </AlfredoStyles.Aviso>
      )}

      {aviso && <AlfredoStyles.Aviso $tom={aviso.tom}>{aviso.texto}</AlfredoStyles.Aviso>}
    </div>
  );
};

export default SecaoDeEntrega;
